/**
 * `GET /site-settings/public` — the few admin settings the client reads before (or without) signing
 * in: the maintenance banner and whether sign-ups are open
 * (`apps/api/src/settings/settings.controller.ts`). Best-effort by design: a settings lookup must
 * never be the reason a page fails, so an unreachable or malformed answer is «no banner, open».
 */
import { API_BASE_URL } from "./http";

export interface PublicSiteSettings {
  maintenanceBanner: string;
  registrationOpen: boolean;
}

const FALLBACK: PublicSiteSettings = { maintenanceBanner: "", registrationOpen: true };

/** The text to show across the top of the site, or `null` when the admin left it empty. Shown as TEXT,
 *  never as HTML: it is typed by an admin, and is data. */
export function maintenanceBannerText(raw: unknown): string | null {
  if (typeof raw !== "string") return null;
  const text = raw.trim();
  return text === "" ? null : text;
}

export async function fetchPublicSiteSettings(): Promise<PublicSiteSettings> {
  try {
    const res = await fetch(`${API_BASE_URL}/site-settings/public`);
    if (!res.ok) return { ...FALLBACK };
    const body = (await res.json()) as Partial<PublicSiteSettings> | null;
    if (!body || typeof body !== "object" || Array.isArray(body)) return { ...FALLBACK };
    return {
      maintenanceBanner: typeof body.maintenanceBanner === "string" ? body.maintenanceBanner : FALLBACK.maintenanceBanner,
      registrationOpen: typeof body.registrationOpen === "boolean" ? body.registrationOpen : FALLBACK.registrationOpen,
    };
  } catch {
    return { ...FALLBACK };
  }
}
